'use client'
import { MapPinIcon, UserIcon, CaretRightIcon, XIcon } from '@phosphor-icons/react/dist/ssr'
import Link from 'next/link'
import { type Dispatch, type JSX, memo, type SetStateAction, useState } from 'react'

import { Drawer } from '@/components/primitives/Drawer/Drawer'
import { cn, isLinkEntry, isNavigationEntry } from '@/lib/utils'

import { Logo } from './Logo'
import { SubMenuHeader } from './SubMenuHeader'
import { LoginButton } from '../LoginButton/LoginButton'
import { MenuLink } from '../Navigation/MenuLink'

import type { NavigationType } from '@/types/contentful/headerType'

export const MobileDrawer = memo(function MobileDrawer({ children, navigation }: { children: JSX.Element; navigation?: NavigationType[] }) {
  const [currentMenu, setCurrentMenu] = useState<string | null>(null)
  const [currentSubMenu, setCurrentSubMenu] = useState<NavigationType | null>(null)
  const [showMenu, setShowMenu] = useState(false)
  const [showSubMenu, setShowSubMenu] = useState(false)

  const resetDrawer = () => {
    setShowMenu(false)
    setCurrentMenu(null)
    setShowSubMenu(false)
    setCurrentSubMenu(null)
  }

  return (
    <Drawer direction="left" aria-label="Mobile Navigation" onOpenChange={(isOpen) => !isOpen && setTimeout(() => resetDrawer(), 300)}>
      <Drawer.Trigger>{children}</Drawer.Trigger>
      <Drawer.Content className={'no-scrollbar relative bottom-0 left-0 h-full overflow-y-auto overflow-x-hidden rounded-r-xl bg-white'}>
        <div className={cn('flex h-full flex-1 flex-col justify-between duration-200', showMenu ? 'opacity-0' : 'opacity-100')}>
          <Drawer.Header className="p-6">
            <div className={cn('flex items-center justify-between')}>
              <Logo />
              <Drawer.Close>
                <XIcon size={24} className="cursor-pointer text-grey-500" onClick={resetDrawer} />
              </Drawer.Close>
            </div>
          </Drawer.Header>

          <Drawer.Body className="flex flex-1 flex-col justify-between overflow-y-auto px-6 pb-4 pt-0">
            <div className="flex h-full flex-1 flex-col outline-none">
              <div className="flex flex-1 flex-col gap-8 overflow-y-auto">
                <ul className="flex flex-col gap-6">
                  {navigation?.map((item) => (
                    <li
                      key={item.sys.id}
                      className="flex cursor-pointer justify-between text-xl font-semibold"
                      onClick={() => {
                        setCurrentMenu(item.fields.title)
                        setShowMenu(true)
                      }}
                    >
                      {item.fields.title} <CaretRightIcon weight="bold" size={20} className="text-grey-500" />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <Drawer.Close>
              <LoginButton className="-mx-2 flex flex-row gap-2">
                <UserIcon size={20} />
                {'My Account'}
              </LoginButton>
            </Drawer.Close>
          </Drawer.Body>
          <Drawer.Footer className="border-t border-t-grey-200 bg-grey-100 py-4">
            <Drawer.Close>
              <Link href="/find-us" className="flex flex-row items-center justify-center gap-2">
                <MapPinIcon size={24} className="text-grey-500" />
                {'Find a Branch'}
              </Link>
            </Drawer.Close>
          </Drawer.Footer>
        </div>
        {/* Menus */}
        {navigation?.map((menuItem) => (
          <Menu
            key={menuItem.sys.id}
            menuItem={menuItem}
            show={showMenu && currentMenu === menuItem.fields.title}
            setShowMenu={setShowMenu}
            setCurrentMenu={setCurrentMenu}
            setCurrentSubMenu={setCurrentSubMenu}
            setShowSubMenu={setShowSubMenu}
          />
        ))}
        <SubMenu subMenu={currentSubMenu} setShowMenu={setShowSubMenu} setCurrentMenu={setCurrentMenu} showSubMenu={showSubMenu} />
      </Drawer.Content>
    </Drawer>
  )
})

const Menu = ({
  menuItem,
  show,
  setShowMenu,
  setCurrentMenu,
  setCurrentSubMenu,
  setShowSubMenu,
}: {
  menuItem: NavigationType
  show: boolean
  setShowMenu: Dispatch<SetStateAction<boolean>>
  setCurrentMenu: Dispatch<SetStateAction<string | null>>
  setCurrentSubMenu: Dispatch<SetStateAction<NavigationType | null>>
  setShowSubMenu: Dispatch<SetStateAction<boolean>>
}) => {
  const title = menuItem.fields.title
  const links = menuItem.fields.links || []

  return (
    <div
      className={cn(
        'no-scrollbar absolute inset-0 z-[1] flex size-full transform-gpu flex-col gap-8 overflow-y-scroll rounded-r-xl bg-white p-6',
        show ? 'delay-300 animate-in fade-in-20 slide-in-from-left-20' : '-translate-x-full',
      )}
    >
      <SubMenuHeader currentMenu={title} setShow={setShowMenu} setCurrentMenu={setCurrentMenu} />
      <div className="flex flex-col gap-6">
        {links.map((child) => {
          if (isLinkEntry(child)) {
            return <MenuLink key={child.sys.id} link={child} />
          }

          if (isNavigationEntry(child)) {
            return (
              <div
                key={child.sys.id}
                className="text-grey-800 flex cursor-pointer items-center justify-between text-xl font-semibold"
                onClick={() => {
                  setCurrentSubMenu(child)
                  setShowSubMenu(true)
                }}
              >
                <span>{child.fields.title}</span>
                <CaretRightIcon weight="bold" size={18} />
              </div>
            )
          }

          return null
        })}
      </div>
    </div>
  )
}

const SubMenu = ({
  subMenu,
  setShowMenu,
  setCurrentMenu,
  showSubMenu,
}: {
  subMenu: NavigationType | null
  setShowMenu: Dispatch<SetStateAction<boolean>>
  setCurrentMenu: Dispatch<SetStateAction<string | null>>
  showSubMenu: boolean
}) => {
  const title = subMenu?.fields?.title || ''
  const links = subMenu?.fields?.links || []

  return (
    <div
      className={cn(
        'no-scrollbar absolute inset-0 z-[2] flex size-full transform-gpu flex-col gap-8 overflow-y-scroll rounded-r-xl bg-white p-6',
        showSubMenu ? 'delay-300 animate-in fade-in-20 slide-in-from-left-20' : '-translate-x-full',
      )}
    >
      <SubMenuHeader currentMenu={title} setShow={setShowMenu} setCurrentMenu={setCurrentMenu} />

      <ul className="flex flex-col gap-4">
        {links.map((link) => {
          if (isLinkEntry(link)) {
            return <MenuLink key={link.sys.id} link={link} />
          }
        })}
      </ul>
    </div>
  )
}
